// src/lib/tenancyTerms.js — "what does the tenant have to pay before they get the keys?"
//
// The single projection for the move-in requirement (owner question, 2026-07-27: "we need
// payment details — what payment is required, 1 month + last month plus deposit?"). Its
// inputs are the three facts core actually records:
//   listing_terms.prepay_first_month   (0213)  — first month's rent up front
//   listing_terms.prepay_last_month    (0213)  — final month's rent up front
//   listing_terms.deposit_amount/_currency (0212) — a fixed sum held for the whole tenancy
//   listing_terms.deposit_months           (1083)  — the SAME deposit stated as a share of a
//                                                    month's rent: 0.5 for „ნახევარი თვე"
//
// ⚠️ 1083: A DEPOSIT IS NOT ALWAYS A SUM. Measured over 70,741 adverts: of the 489 naming a
// deposit, 115 give a money figure, 87 give a NUMBER OF MONTHS and 7 give HALF a month. This
// projection read only the sum, so a „დეპოზიტი: ნახევარი თვე" listing rendered a move-in
// bill with no deposit in it — an under-statement of what the tenant must produce, which is
// worse than no figure at all.
//
// 🔑 `deposit_months` IS MONTHS (0.5), NEVER A PERCENT (50). It is multiplied by the rent
// here; a percent stored in it would produce fifty months of rent as the deposit.
//
// DERIVED, NEVER STORED — the same rule the cooperation commission follows. A stored total
// goes stale the moment the rent changes, and the gallery's "წინასწარ" line already
// recomputes it on every render; two stored copies of one arithmetic result is how the
// currency bugs of 2026-07-19 started.
//
// ⚠️ CURRENCY IS NOT ASSUMED. The deposit carries its own currency by design (0212), and it
// genuinely differs from the rent in real ads (a ₾ rent with a $ deposit). When the two
// disagree and we have no honest way to add them, `total` is null and `mixed` is true — the
// caller shows the PARTS and no sum, rather than a number that silently adds dollars to lari.
// Converting here would need an fx rate, and a move-in figure quoted to a client at a
// yesterday rate is worse than no figure at all.
//
// Unknown ≠ zero, everywhere: a null prepay flag means nobody asked the owner, so the part
// is OMITTED from the list and `complete` goes false. A caller must never render a total
// from an incomplete picture as though it were the whole bill.

// One part of the bill. `kind` is stable for callers that want their own labels/icons;
// `months` counts rent-multiples so a caller can show "×2" without re-deriving it.
function part(kind, amount, currency) { return { kind, amount, currency } }

// ── THE CARD'S ONE-LINE FORM (2026-09-12) ────────────────────────────────────────────────
// The move-in requirement reached exactly one screen — the property modal — while the CARD,
// which is where an agent actually scans, said nothing about it. 753 listings carry a
// prepayment or deposit read out of their own advert and none of it was visible.
//
// 🔑 LABELLED HERE, BESIDE THE PROJECTION, NOT IN THE CARD. `moveInPayment` already decides
// what is owed and what is unknown; a second summariser in a component would be a second
// opinion on the same numbers, and the two would drift the first time one of them learned
// about a new field (this file has watched that happen with the commission).
//
// Returns null when nothing was recorded — the caller renders its "ask the owner" door
// instead, exactly as it does for restrictions. Never returns "0" or an empty pill: an
// unanswered question and a free move-in are different facts.
export function moveInLabel(m, tr) {
  if (!m || !m.asked || !m.parts.length) return null
  const L = { first_month: 'desk_terms_prepay_first', last_month: 'desk_terms_prepay_last',
              deposit: 'desk_terms_deposit_short' }
  const rows = m.parts.map(p => ({
    key: p.kind,
    label: p.amount != null && p.currency
      ? `${tr(L[p.kind])} — ${Number(p.amount).toLocaleString('en-US')} ${p.currency}`
      : tr(L[p.kind]),
  }))
  // The headline is the MONTHS when they are whole or a half — „წინასწარ 2.5 თვე" is a
  // number an agent can hold in their head — and falls back to the part count when the
  // rent is unknown and nothing can be multiplied.
  const head = m.total != null && m.currency
    ? `${Number(m.total).toLocaleString('en-US')} ${m.currency}`
    : (m.months > 0 ? tr('desk_terms_summary_months').replace('{n}', String(m.months))
                    : tr('desk_movein_heading'))
  // ⚠️ `complete` is the ONLY case where the total is the whole bill (moveInPayment's own
  // rule: every part priced, one currency, and both prepay flags actually answered). When
  // it is false the number is a FLOOR, and the tilde is what says so — a bare figure an
  // agent quotes to a client must never imply the list was finished.
  return { head: m.complete ? head : `~${head}`, rows, complete: !!m.complete, months: m.months }
}

export function moveInPayment({
  prepayFirst = null, prepayLast = null,
  depositAmount = null, depositCurrency = null, depositMonths = null,
  rent = null, rentCurrency = null,
} = {}) {
  const num = v => {
    const n = typeof v === 'string' ? parseFloat(v) : v
    return typeof n === 'number' && isFinite(n) && n > 0 ? n : null
  }
  const r = num(rent)
  const dep = num(depositAmount)
  // A share-based deposit needs the rent to become money, exactly like the prepay months
  // above — and like them, the REQUIREMENT is a fact even when the rent is unknown.
  const depMon = (() => {
    const n = typeof depositMonths === 'string' ? parseFloat(depositMonths) : depositMonths
    return typeof n === 'number' && isFinite(n) && n > 0 ? n : null
  })()
  const parts = []
  let months = 0
  // A `true` flag is a stated requirement even when we don't know the rent — the REQUIREMENT
  // is the fact; the money is the projection. So the part is listed either way, with a null
  // amount when the rent is unknown.
  if (prepayFirst === true) { parts.push(part('first_month', r, rentCurrency)); months++ }
  if (prepayLast === true) { parts.push(part('last_month', r, rentCurrency)); months++ }
  // The sum wins when both are recorded: it is what the seller actually named in money,
  // and the share is then a restatement of it (1083 stores both without contradiction).
  if (dep != null) parts.push(part('deposit', dep, depositCurrency || null))
  else if (depMon != null) {
    // Rounded to the cent, never left as a repeating fraction: 0.5 × 1,333 is a bill an
    // agent reads aloud to a client.
    parts.push(part('deposit', r != null ? Math.round(r * depMon * 100) / 100 : null,
                    r != null ? rentCurrency : null))
    months += depMon
  }

  // "Did anyone actually ask?" — all three unanswered is a blank record, not a free move-in.
  const asked = prepayFirst != null || prepayLast != null || dep != null || depMon != null
  // Every listed part must have both an amount and a currency before a sum means anything.
  const priced = parts.filter(p => p.amount != null && p.currency)
  const mixed = new Set(priced.map(p => p.currency)).size > 1
  const total = (!parts.length || priced.length !== parts.length || mixed)
    ? null
    : priced.reduce((s, p) => s + p.amount, 0)
  const currency = total != null ? priced[0].currency : null

  return {
    parts,                      // [{kind, amount|null, currency|null}] in payment order
    months,                     // how many months of rent are demanded up front
    total, currency, mixed,
    asked,                      // false = nothing recorded at all (never render "0")
    // true only when every recorded requirement is priced in one currency — the ONLY
    // case where `total` is the whole bill rather than part of it.
    complete: total != null && prepayFirst != null && prepayLast != null,
  }
}
